<script setup lang="ts">
// Lists of elements
const administratorLabels = [
  'nameAndSurname',
  'email',
  'country',
  'password',
  'phoneNumber',
  'organisationName',
  'positionInOrganisation',
]
const newsletterLabels = ['email', 'IP']
const emailCommunicationLabels = ['name', 'email', 'others']
const rightsOfSubjectsLabels = ['first', 'second', 'third', 'fourth']

// Repeatable paragraphs without lists
const firstGroupOfParagraphsWithoutLists = [
  {
    name: 'administratorParagraphs',
    nameAppendix: 'labelAppendix',
    endings: ['first', 'second', 'third'],
  },
  {
    name: 'rangeParagraphs',
    endings: ['first'],
  },
  {
    name: 'resourceParagraphs',
    endings: ['first'],
  },
  {
    name: 'cookiesParagraphs',
    endings: ['first', 'second'],
  },
]
const secondGroupOfParagraphsWithoutLists = [
  {
    name: 'wayOfGatheringPersonalDataParagraphs',
    endings: ['first'],
  },
  {
    name: 'categoriesOfReceiversParagraphs',
    endings: ['first', 'second'],
  },
  {
    name: 'durationOfKeepingDataParagraphs',
    endings: ['first', 'second'],
  },
]
</script>

<template>
  <section class="container">
    <div class="mt-14 flex flex-row px-4">
      <div class="mx-auto lg:basis-3/5">
        <h2 class="mb-4 text-4xl font-bold">
          {{ $t('forCustomers.privacy-policy.declaration') }}
        </h2>
        <p class="mb-4 text-xl font-bold">
          {{ $t('forCustomers.privacy-policy.processingOfPersonalData') }}
        </p>
        <div
          v-for="paragraph in firstGroupOfParagraphsWithoutLists"
          :key="paragraph.name"
        >
          <h2 class="mb-4 text-xl font-bold">
            {{
              $t(
                'forCustomers.privacy-policy.sections.' +
                  paragraph.name +
                  '.label'
              )
            }}
            <span v-show="paragraph.nameAppendix" class="font-normal">
              {{
                $t(
                  'forCustomers.privacy-policy.sections.administratorParagraphs.labelAppendix'
                )
              }}
            </span>
          </h2>
          <p v-for="ending in paragraph.endings" :key="ending" class="mb-4">
            {{
              $t(
                'forCustomers.privacy-policy.sections.' +
                  paragraph.name +
                  '.' +
                  ending
              )
            }}
          </p>
        </div>
        <h2 class="mb-4 text-xl font-bold">
          {{
            $t(
              'forCustomers.privacy-policy.sections.whatIsGatheredParagraphs.label'
            )
          }}
        </h2>
        <p class="mb-2">
          {{
            $t(
              'forCustomers.privacy-policy.sections.whatIsGatheredParagraphs.first'
            )
          }}
        </p>
        <ol class="ml-12 list-decimal">
          <li v-for="label in administratorLabels" :key="label" class="mb-1">
            {{
              $t(
                'forCustomers.privacy-policy.sections.whatIsGatheredParagraphs.administratorGathering.' +
                  label
              )
            }}
          </li>
        </ol>
        <pre class="mb-4"></pre>
        <p class="mb-4">
          {{
            $t(
              'forCustomers.privacy-policy.sections.whatIsGatheredParagraphs.second'
            )
          }}
        </p>
        <p class="mb-2">
          {{
            $t(
              'forCustomers.privacy-policy.sections.whatIsGatheredParagraphs.third'
            )
          }}
        </p>
        <ol class="ml-12 list-decimal">
          <li v-for="label in newsletterLabels" :key="label" class="mb-1">
            {{
              $t(
                'forCustomers.privacy-policy.sections.whatIsGatheredParagraphs.newsletterGathering.' +
                  label
              )
            }}
          </li>
        </ol>
        <pre class="mb-4"></pre>
        <p class="mb-4">
          {{
            $t(
              'forCustomers.privacy-policy.sections.whatIsGatheredParagraphs.fourth'
            )
          }}
        </p>
        <p class="mb-2">
          {{
            $t(
              'forCustomers.privacy-policy.sections.whatIsGatheredParagraphs.fifth'
            )
          }}
        </p>
        <ol class="ml-12 list-decimal">
          <li
            v-for="label in emailCommunicationLabels"
            :key="label"
            class="mb-1"
          >
            {{
              $t(
                'forCustomers.privacy-policy.sections.whatIsGatheredParagraphs.emailCommunicationGathering.' +
                  label
              )
            }}
          </li>
        </ol>
        <pre class="mb-4"></pre>
        <p class="mb-4">
          {{
            $t(
              'forCustomers.privacy-policy.sections.whatIsGatheredParagraphs.sixth'
            )
          }}
        </p>
        <div
          v-for="paragraph in secondGroupOfParagraphsWithoutLists"
          :key="paragraph.name"
        >
          <h2 class="mb-4 text-xl font-bold">
            {{
              $t(
                'forCustomers.privacy-policy.sections.' +
                  paragraph.name +
                  '.label'
              )
            }}
          </h2>
          <p v-for="ending in paragraph.endings" :key="ending" class="mb-4">
            {{
              $t(
                'forCustomers.privacy-policy.sections.' +
                  paragraph.name +
                  '.' +
                  ending
              )
            }}
          </p>
        </div>
        <h2 class="mb-4 text-xl font-bold">
          {{
            $t(
              'forCustomers.privacy-policy.sections.rightsOfSubjectsParagraphs.label'
            )
          }}
        </h2>
        <p class="mb-2">
          {{
            $t(
              'forCustomers.privacy-policy.sections.rightsOfSubjectsParagraphs.first'
            )
          }}
        </p>
        <ul class="ml-12 list-disc">
          <li v-for="label in rightsOfSubjectsLabels" :key="label" class="mb-1">
            {{
              $t(
                'forCustomers.privacy-policy.sections.rightsOfSubjectsParagraphs.rights.' +
                  label
              )
            }}
          </li>
        </ul>
        <pre class="mb-4"></pre>
        <p class="mb-4">
          {{
            $t(
              'forCustomers.privacy-policy.sections.rightsOfSubjectsParagraphs.second'
            )
          }}
        </p>
        <p class="mb-4 mt-8">
          {{ $t('forCustomers.privacy-policy.sections.lastUpdate') }}
        </p>
      </div>
    </div>
    <div class="my-32"></div>
  </section>
</template>

<style scoped></style>
